import { useContext } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import classnames from 'classnames'
import { ActiveOverlayContext } from '../Routing/OverlayContext'
import { Text, StatusIcon } from '../..'
import { Button, ComponentTrackingType } from '@homebotapp/hb-react-component-catalog'
import { useSafeVerificationFlow } from '../Questions/hooks/useSafeVerificationFlow'
import { TopicKey } from '../../../constants/customerMessages'
import { PartnerType } from '../VerificationFlow/types'
import {
  selectPreferredLoanOfficerMembership,
  selectPreferredRealEstateAgentMembership
} from '../../../store/selectors/clientTeamMemberships'

import styles from './OutreachButton.module.scss'

export interface OutreachButtonProps {
  className?: string
  requested?: boolean
  success: boolean
  failure: boolean
  buttonMessage: { id: string; defaultMessage: string }
  resultMessage: string
  handleClick?: (args) => void
  secondary?: boolean
  children?: React.ReactNode
  iconStart?: React.ReactNode
  colorScheme?: string
  iconLeft?: React.ReactNode
  iconRight?: React.ReactNode
  tracking?: ComponentTrackingType
  // Verification flow props
  requiresPartnerVerification?: boolean
  verificationMessage?: string
  verificationTopicKey?: TopicKey
  defaultTopicKey?: TopicKey
  partnerType?: PartnerType
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  restProps?: any
}

export const OutreachButton = ({
  buttonMessage,
  className,
  failure,
  handleClick,
  requested,
  colorScheme,
  resultMessage,
  success,
  tracking,
  // Verification flow props
  requiresPartnerVerification = false,
  verificationMessage,
  verificationTopicKey,
  defaultTopicKey,
  partnerType = 'LoanOfficer',
  restProps // I know people hate this but it's for any other props the DS/chakra button might take
}: OutreachButtonProps) => {
  const { exitingPanel } = useContext(ActiveOverlayContext) || {
    exitingPanel: false
  }

  const intl = useIntl()
  const { state: verificationState, startVerification } = useSafeVerificationFlow()
  const resultTextColor = success ? 'success' : failure ? 'warning' : 'neutralCool'

  const handleButtonClick = args => {
    // If verification is required and we're in the idle state, start verification
    if (requiresPartnerVerification && verificationState.currentStep === 'idle') {
      const message = verificationMessage || intl.formatMessage(buttonMessage)
      const topicKey = verificationTopicKey || defaultTopicKey
      const fallbackTopicKey = defaultTopicKey || verificationTopicKey

      if (topicKey && fallbackTopicKey) {
        startVerification(message, topicKey, fallbackTopicKey, partnerType)
        return
      }
    }

    // Otherwise proceed with normal click handling
    handleClick(args)
  }

  return (
    <div data-qa='outreach-btn-component' className={classnames(styles.container, className)}>
      {!success && !failure && (
        <Button
          data-qa='outreach-btn'
          className={styles.button}
          colorScheme={colorScheme}
          isLoading={requested}
          onClick={handleButtonClick}
          tracking={tracking}
          {...restProps}
        >
          {intl.formatMessage(buttonMessage)}
        </Button>
      )}
      {(success || failure) && (
        <div
          className={classnames(
            exitingPanel && styles.disableShow,
            styles.slideOpenContainer,
            (success || failure) && styles.ready
          )}
        >
          {/* @ts-ignore */}
          <StatusIcon
            className={classnames(styles.icon, failure && styles.failure)}
            failure={failure}
            spin={!success && !failure}
            success={success}
          />
          <Text
            variant={resultTextColor}
            variantCount='700'
            size='s'
            element='span'
            className={styles.resultText}
            weight='semibold'
          >
            {resultMessage}
          </Text>
        </div>
      )}
    </div>
  )
}
